import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import isFuture from "date-fns/isFuture";
import { getVendor, SlugValidation, VendorConfigIds } from "manifest-components/src/classes/vendorConfig";
import { AppContext } from "../../context/App/appProvider";
import Session from "../../utils/session";
import { route, RoutePaths, useTypedLocation, useTypedNavigate } from "../../routes/RoutePaths";
import WelcomeScreen from "./layouts/twoColumnCarousel/WelcomeScreen";
import BroseLogo from "./logos/Brose.png";
import OneElevenLogo from "./logos/oneEleven.png";
import PerksLogo from "./logos/perks.png";
import ThatchLogo from "./logos/thatch.png";
import DemoLogo from "./logos/demo-logo.png";
import PEHPLogo from "./logos/PEHP.png";
import CarbonCollectiveLogo from "./logos/carbon-collective.png";
import SunnyDayFundLogo from "./logos/sunnyday_fund_logo.png";
import SendaLogo from "./logos/Senda Logo.png";
import AnthesisLogo from "./logos/Anthesis_Logo.png";
import EveryLogo from "./logos/Every logo_black.png";
import CPEHNLogo from "./logos/2001-CPEHN-longname-facebook.png";
import AIMLogo from "./logos/AIM logo.png";
import StandEarthLogo from "./logos/Stand Earth Logo.png";
import CACLogo from "./logos/Climate action campaign.png";
import ILDLogo from "./logos/Immigrant legal defense.png";
import AIGenLogo from "./logos/2151-Aigen_Logo_Gradient.png";
import ProjectDrawdownLogo from "./logos/project-drawdown.png";
import SynergiLogo from "./logos/synergi-logo.webp";
import "../../utils/analytics";
import EmailSurveyResultPopup from "../../components/EmailSurveyResultPopup";
import { SurveyResult } from "../Vendor/typings/Survey";
import { surveyResultsArray } from "../Vendor/constants";
import { isProdEnvironment } from "../../env";

// TODO: move this to manifest-components later
export const logoToVendor: Record<VendorConfigIds, string> = {
  brose: BroseLogo,
  oneeleven: OneElevenLogo,
  perks: PerksLogo,
  thatch: ThatchLogo,
  demo: DemoLogo,
  pehp: PEHPLogo,
  carboncollective: CarbonCollectiveLogo,
  senda: SendaLogo,
  sunnydayfund: SunnyDayFundLogo,
  anthesis: AnthesisLogo,
  every: EveryLogo,
  cpehn: CPEHNLogo,
  maplan: DemoLogo,
  planchange: DemoLogo,
  cctestemployer: DemoLogo,
  aim: AIMLogo,
  ers: AIMLogo,
  stand: StandEarthLogo,
  cac: CACLogo,
  ild: ILDLogo,
  projectdrawdown: ProjectDrawdownLogo,
  aigen: AIGenLogo,
  synergillc: SynergiLogo,
};

export type WelcomeControllerType = ReturnType<typeof WelcomeController>;

type WelcomeControllerProps = {
  hideWelcomeVideo?: boolean;
};

export function WelcomeController(props: WelcomeControllerProps = {}) {
  const { hideWelcomeVideo } = props;
  const { setVendorName, getCurrentEmploymentStatus, employerInfo, employerInfoFetched } = useContext(AppContext);
  const navigate = useTypedNavigate();
  const location = useTypedLocation();
  const { vendorName } = useParams<{ vendorName: VendorConfigIds }>();
  const showEmployerSelection = location?.state?.showEmployerSelection || false;
  const [loading, setLoading] = useState(true);
  const [confirmEmployer, setConfirmEmployer] = useState(showEmployerSelection);
  const welcomeVideoRef = useRef<MutationObserver>();
  const [isPrelaunch, setIsPrelaunch] = useState<boolean>(false);

  const [steps, setSteps] = useState<
    {
      description: string;
      icon: Element | null;
    }[]
  >([]);

  const goToAccountCreationScreen = (isCurrentEmployer: boolean) => () => {
    navigate(route(RoutePaths.createAccount), {
      state: {
        isCurrentEmployer,
        vendorName,
        isEmployerUrl: employerInfo?.companyType === "EMP",
        companyType: employerInfo?.companyType,
      },
    });
  };

  const populateHomePage = useCallback(async () => {
    Session.vendorName = vendorName || "";
    if (!vendorName) return setLoading(false);
    if (!employerInfoFetched) {
      await getCurrentEmploymentStatus(vendorName);
      return;
    }
    // If the vendor name doesn't exist, just redirect back to home
    if (!employerInfo || !employerInfo.adminStatus) {
      navigate(RoutePaths.welcome);
      return;
    }

    setIsPrelaunch(isFuture(new Date(employerInfo.launchDate)) || employerInfo.adminStatus === "INR");

    if (
      (vendorName === "demo" ||
        vendorName === "maplan" ||
        vendorName === "planchange" ||
        vendorName === "cctestemployer") &&
      isProdEnvironment()
    )
      return navigate(RoutePaths.welcome);

    if (employerInfoFetched && loading) {
      setLoading(false);
    }
  }, [loading, vendorName, employerInfoFetched, getCurrentEmploymentStatus]);

  function login() {
    navigate(route(RoutePaths.login));
  }

  function createAccount() {
    if (employerInfo && employerInfo?.companyType === "EMP" && employerInfo?.adminStatus === "RCM") {
      return setConfirmEmployer(true);
    }
    goToAccountCreationScreen(false)();
  }

  const removeWelcomeVideoDisplay = useCallback(() => {
    welcomeVideoRef.current?.disconnect();
    setTimeout(() => {
      const beaconEl = document.querySelector("#beacon-container")! as HTMLDivElement;
      const videoEl = document.querySelector("#tolstoyWidgetElement")! as HTMLDivElement;
      if (beaconEl) {
        beaconEl.style.opacity = "1";
        beaconEl.style.pointerEvents = "all";
      }
      if (videoEl) {
        videoEl.classList.remove("show");
      }
    }, 100);
  }, []);

  const setupWelcomeVideoDisplay = useCallback(() => {
    const observer = new MutationObserver(() => {
      const beaconEl = document.querySelector("#beacon-container")! as HTMLDivElement;
      const videoEl = document.querySelector("#tolstoyWidgetElement")! as HTMLDivElement;
      if (beaconEl) {
        beaconEl.style.opacity = "0";
        beaconEl.style.pointerEvents = "none";
      }
      if (videoEl) {
        videoEl.classList.add("show");
      }
    });
    observer.observe(document, {
      childList: true,
      subtree: true,
    });
    welcomeVideoRef.current = observer;
  }, []);

  const vendorConfig = useMemo(() => {
    return getVendor(vendorName as any);
  }, [vendorName]);

  useEffect(() => {
    const allSteps = [
      {
        description: "Locate your old retirement accounts.",
        icon: null,
      },
      {
        description: "Identify the best account to consolidate your savings.",
        icon: null,
      },
      {
        description: "We’ll handle the rest and help you track your transfer.",
        icon: null,
      },
    ];
    setSteps(allSteps);
    setVendorName(vendorName);
  }, []);

  useEffect(() => {
    populateHomePage();
  }, [populateHomePage]);

  useEffect(() => {
    if (hideWelcomeVideo) return;
    if (vendorConfig?.id === "pehp") return; // for pehp landing page, we do not show the tolstoy popup
    setupWelcomeVideoDisplay();
    return () => {
      removeWelcomeVideoDisplay();
    };
  }, [vendorConfig]);

  const WelcomePopup: FunctionComponent = () => {
    const queryParams = new URLSearchParams(location.search);
    const showPopup = queryParams.get("showPopup");
    const popupContent = queryParams.get("popupContent");

    if (!showPopup || !popupContent) return null;

    if (surveyResultsArray.indexOf(popupContent) < 0) {
      navigate(route(RoutePaths.welcomeWithVendor, { vendorName: vendorName || "" }));
      return null;
    }
    if (showPopup) {
      return (
        <EmailSurveyResultPopup
          result={popupContent as SurveyResult}
          goToVendorSite={() => navigate(route(RoutePaths.welcomeWithVendor, { vendorName: vendorName || "" }))}
        />
      );
    }

    return null;
  };

  return {
    confirmEmployer,
    setConfirmEmployer,
    employerInfo,
    goToAccountCreationScreen,
    createAccount,
    login,
    steps,
    vendorName,
    vendorConfig,
    WelcomePopup,
    isPrelaunch,
    employerInfoFetched,
    loading,
  };
}

export default function WelcomePage() {
  const controller = WelcomeController();
  const { employerInfoFetched, employerInfo } = controller;

  if (employerInfoFetched && employerInfo?.companyName && employerInfo?.adminStatus) {
    return <WelcomeScreen {...controller} logoUrl={employerInfo.presignedLogoUrl || ""} employerInfo={employerInfo} />;
  }
  return <WelcomeScreen {...controller} employerInfo={{} as SlugValidation} confirmEmployer={false} />;
}
